import { useState } from "react";
import { useParams, useNavigate, generatePath } from "react-router-dom";
import routes from "@/constants/routes";
import { Stack, Button, Grid2 as Grid, Container } from "@mui/material";
import { Menu } from "@/types/menus";
import { useAccessControl, withAccessControl } from "@/features/access-control";
import UpdateMenuDrawer from "./components/UpdateMenuDrawer";
import PageHeader from "@/components/PageHeader";
import { useFetchMenusQuery } from "@/features/organizations/api/menusApi";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import MenuCard from "./components/MenuCard";
import CreateMenuDrawer from "./components/CreateMenuDrawer";
import OrganizationMenuSkeleton from "./components/OrganizationMenuSkeleton";
import AvailableQuota from "@/components/AvailableQuota";
import useOrganizationPlan from "@/hooks/useOrganizationPlan";
import { useAppSelector } from "@/hooks";

const OrganizationMenus = () => {
  const container = useAppSelector((state) => state.ui?.container);
  const navigate = useNavigate();
  const { hasAccess } = useAccessControl();
  const { checkFeatureLimit, openUpgradePopUp } = useOrganizationPlan("menu");
  const { organizationId } = useParams<{ organizationId: string }>();
  const [selectedMenu, setSelectedMenu] = useState<Menu>();
  const [isNewMenuDrawerOpen, setIsNewMenuDrawerOpen] = useState(false);
  const { isFetching, data } = useFetchMenusQuery(organizationId ?? skipToken);

  const onCloseMenuSettings = () => {
    setSelectedMenu(undefined);
  };

  const handleMenuClick = (menu: Menu) => {
    if (!menu.flow || menu.flow.length === 0) {
      navigate(generatePath(routes.organizations.catalogo.editor, { organizationId: organizationId ?? "", menuId: menu.id }));
    } else {
      setSelectedMenu(menu);
    }
  };

  const Actions = () => {
    if (hasAccess("create.menu")) {
      return (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="center" alignItems="center">
          <AvailableQuota feature="menu" usedQuota={data?.length} />
          <Button
            size="large"
            color="success"
            onClick={() => {
              if (checkFeatureLimit(data?.length)) {
                navigate(generatePath(routes.organizations.catalogo.create, { organizationId }));
              } else {
                openUpgradePopUp();
              }
            }}
          >
            Crear Menú
          </Button>
        </Stack>
      );
    }

    return null;
  };

  return (
    <Stack spacing={3}>
      <PageHeader
        title="Menús"
        action={<Actions />}
      // banner="https://firebasestorage.googleapis.com/v0/b/menuapp-b44a8.appspot.com/o/assets%2Fnew-menu-banner.webp?alt=media&token=16477ef1-b16d-44d7-aa48-5b03f3cabe3e"
      />
      <Stack
        sx={{
          flex: 1,
        }}
      >
        <Container
          maxWidth={container === 'fixed' ? 'lg' : false}
          sx={{ height: '100%' }}
        >
          {isFetching && !data ? (
            <Grid container spacing={3}>
              {["", ""].map((s, i) => (
                <Grid key={`skelrton-${i}`} size={{ xs: 12, lg: 6, xl: 4 }}>
                  <OrganizationMenuSkeleton />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Grid container spacing={3}>
              {data?.map((menu) => {
                const isCatalog = !menu.flow || menu.flow.length === 0;
                return (
                  <Grid key={menu.id} size={{ xs: 12, md: 6, xl: 4 }}>
                    <MenuCard
                      title={menu.title}
                      description={menu?.description}
                      isPublished={menu.isPublished}
                      onClick={() => handleMenuClick(menu)}
                      onSettingsClick={isCatalog ? () => setSelectedMenu(menu) : undefined}
                    />
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Container>
      </Stack>
      <UpdateMenuDrawer selectedMenu={selectedMenu} onClose={onCloseMenuSettings} />
      <CreateMenuDrawer isOpen={isNewMenuDrawerOpen} onClose={() => setIsNewMenuDrawerOpen(false)} />
    </Stack>
  );
};

export default withAccessControl(OrganizationMenus, "view.menu");
